import React, { useEffect, useRef, useState } from 'react';
import { X, Loader2, ShieldCheck, Smartphone, ArrowRight, CreditCard } from 'lucide-react';
import api from '../lib/api';
import { getBootLocale } from '../i18n';

/**
 * Optional breakdown shown ABOVE the Tranzila iframe — the gateway's hosted
 * page doesn't display item names or VAT splits, so we render that header
 * ourselves to make the flow look like one screen. Only shown when the
 * caller passes `details`.
 */
export interface PaymentBreakdownLine {
  /** Item label, e.g. "Apple Pay — אמצעי תשלום לתורמים". */
  label: string;
  /** Pre-VAT amount for this line in ILS. */
  amount: number;
  /** Optional note shown next to the price (e.g. "חד-פעמי", "חודשי"). */
  note?: string;
}

export interface PaymentBreakdown {
  /** Top-level title shown above the line items, e.g. "חבילת Pro". */
  item: string;
  /** Individual line items rendered as a vertical list. Optional. */
  lines?: PaymentBreakdownLine[];
  /** Pre-VAT total in ILS. */
  net: number;
  /** VAT amount in ILS (we always treat as 18%, but pass it explicitly). */
  vat: number;
  /** Final amount the donor will be charged (= net + vat). */
  gross: number;
  /** Optional second line shown under the item (e.g. "מהחודש הבא: ₪471/חודש"). */
  subline?: string;
}

/**
 * Donation context passed from donation surfaces (PublicCampaign,
 * NewDonationDialog, PublicQuote). When present AND the system has GamaPay
 * BIT configured, the modal renders a "שלם ב-BIT" toggle that opens the
 * BIT-via-SMS flow instead of the credit-card iframe.
 *
 * Billing surfaces (Amutech subscription payment) DO NOT pass this — BIT
 * is donations-only.
 */
export interface BitDonationContext {
  orgId: string;
  amount: number;
  campaignId?: string;
  contactId?: string;
  ambassadorSlug?: string;
  donorName?: string;
  donorEmail?: string;
  donorPhone?: string;
  // Donor ת״ז captured by the campaign form. When it's a valid 9-digit ID the
  // BIT gate is pre-filled (and its input hidden) so the donor isn't asked for
  // it again before the SMS is sent.
  donorIdNumber?: string;
  // Mailing-campaign attribution. When the donor reaches the page via a
  // link from email / SMS / WhatsApp, the URL carries `?mc=<id>` and the
  // donate surface forwards it here so /payments/bit/initiate stashes it
  // on the PaymentSession. The Bit callback then stamps it onto the
  // Donation so the income table's "דיוור" column resolves.
  mailCampaignId?: string;
  // Call-center attribution token (`?tc=` on a telephonist's quick-send link).
  tc?: string;
  // Recurring / standing-order donation. BIT has no stored token, so a
  // monthly charge can't run through it — when set, the modal hides the
  // "שלם ב-BIT" toggle entirely and the donor stays on the card iframe.
  recurring?: boolean;
  // Donor's answers to the campaign form's custom fields (keyed by field.id).
  // Forwarded to /payments/bit/initiate so the Bit flow persists them on the
  // Donation — parity with the Tranzila/Hyp iframe flow (which sends them via
  // the /public/payment-url query string).
  customFields?: Record<string, any>;
  // Donor opted to stay anonymous — forwarded to /payments/bit/initiate so the
  // resulting Donation is masked on the public lists (parity with the card flow's
  // `anonymous` query param).
  anonymous?: boolean;
}

interface PaymentIframeModalProps {
  url: string | null;
  open: boolean;
  onClose: () => void;
  onSuccess: (data: Record<string, string>) => void;
  onFail?: (data: Record<string, string>) => void;
  title?: string;
  details?: PaymentBreakdown;
  bitContext?: BitDonationContext;
  /** Open straight into the BIT flow (used by the dedicated "pay with bit"
   *  button) instead of the credit-card iframe. */
  startInBit?: boolean;
}

type Mode = 'card' | 'bit-id-entry' | 'bit-confirming' | 'bit-waiting' | 'bit-done' | 'bit-failed';

export default function PaymentIframeModal({
  url,
  open,
  onClose,
  onSuccess,
  onFail,
  // Default title is locale-aware. When the caller passes an explicit title
  // we honor it as-is (legacy callers stay unaffected). For everyone else,
  // we pick Hebrew or English based on the boot locale (?lang=). The iframe
  // itself is told the same locale via lang=il/en on the backend URL.
  title,
  details,
  bitContext,
  startInBit,
}: PaymentIframeModalProps) {
  const resolvedTitle =
    title ?? (getBootLocale() === 'en' ? 'Secure Payment' : 'תשלום מאובטח');
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeLoading, setIframeLoading] = useState(true);
  const handledRef = useRef(false);
  const onSuccessRef = useRef(onSuccess);
  const onFailRef = useRef(onFail);
  onSuccessRef.current = onSuccess;
  onFailRef.current = onFail;

  // ─── BIT availability + flow state ──────────────────────────────────────
  // We probe /payments/providers-available once per modal-open. The flag is
  // sticky across renders within the same open. If BIT isn't configured by
  // the super-admin, the button never renders.
  const [bitAvailable, setBitAvailable] = useState(false);
  const [mode, setMode] = useState<Mode>('card');
  const [bitErr, setBitErr] = useState<string | null>(null);
  const [bitSessionId, setBitSessionId] = useState<string | null>(null);
  const [bitSchemes, setBitSchemes] = useState<{ ios?: string | null; android?: string | null }>({});
  // ת.ז. is mandatory before we ship a BIT SMS — required for the donation
  // receipt and for back-linking the donor to a Contact card. We collect it
  // in a small gate UI between clicking "שלם ב-BIT" and the actual /initiate
  // POST, so the value is in the same request that triggers the SMS.
  const [bitIdNumber, setBitIdNumber] = useState('');

  useEffect(() => {
    if (!open) return;
    handledRef.current = false;
    setIframeLoading(true);
    // Open straight into the BIT flow when requested by the dedicated bit
    // button; fall back to the ת.ז.-entry's own phone guard when no phone.
    if (startInBit && bitContext) {
      if (bitContext.donorPhone) { setMode('bit-id-entry'); setBitErr(null); }
      else { setMode('bit-failed'); setBitErr('נדרש טלפון נייד לקבלת SMS לתשלום ב-BIT'); }
    } else {
      setMode('card');
      setBitErr(null);
    }
    setBitSessionId(null);
    setBitSchemes({});
    // Pre-fill the ת״ז from the campaign form when it's already a valid 9-digit
    // ID — the gate then shows it filled (input hidden) instead of asking again.
    {
      const pre = (bitContext?.donorIdNumber || '').replace(/\D/g, '');
      setBitIdNumber(/^\d{9}$/.test(pre) && pre !== '000000000' ? pre : '');
    }

    // Probe BIT availability whenever the modal opens with a bitContext.
    // The probe is per-org because BIT credentials live on the org's
    // terminal — different orgs may have it on/off independently.
    if (bitContext?.orgId) {
      (async () => {
        try {
          const r = await api.get('/payments/providers-available', {
            params: { orgId: bitContext.orgId },
          });
          setBitAvailable(Boolean(r.data?.bit));
        } catch {
          setBitAvailable(false);
        }
      })();
    } else {
      setBitAvailable(false);
    }

    // Fallback: some payment gateways don't fire onLoad reliably for cross-origin redirects
    const t = setTimeout(() => setIframeLoading(false), 3000);

    const handler = (ev: MessageEvent) => {
      const data: any = ev.data;
      if (!data || typeof data !== 'object') return;
      if (data.type === 'amutech-payment-complete' && !handledRef.current) {
        handledRef.current = true;
        onSuccessRef.current(data.payload || {});
      } else if (data.type === 'amutech-payment-failed' && !handledRef.current) {
        handledRef.current = true;
        onFailRef.current?.(data.payload || {});
      }
    };

    window.addEventListener('message', handler);
    return () => {
      window.removeEventListener('message', handler);
      clearTimeout(t);
    };
  }, [open, bitContext?.orgId, startInBit]);

  // ─── Card flow: safety net poll on OUR record of the charge ─────────────
  // The success screen above depends on the gateway navigating its iframe back
  // to /payments/complete so that page can postMessage to us. That return trip
  // lives entirely in the donor's browser and has broken before (Aug-2026: our
  // own X-Frame-Options blocked the page inside Tranzila's iframe on
  // Safari/Firefox — donors were charged and saw nothing). The charge itself
  // reaches us on a separate server-to-server notify, so we also ask the
  // backend about this exact session and can finish the flow without any
  // redirect at all.
  //
  // Only a confirmed 'completed' is ever acted on. A decline deliberately does
  // NOT close the modal: the gateway shows its own error inside the iframe and
  // the donor can retry there, and we never want a poll to be able to turn a
  // failed charge into a thank-you screen.
  //
  // `order_id` is the PaymentSession id and is present on the Tranzila iframe
  // URL only, so this quietly no-ops for every other provider. handledRef is
  // shared with the postMessage handler, so whichever path arrives first wins
  // and onSuccess still fires exactly once.
  useEffect(() => {
    if (!open || mode !== 'card' || !url) return;
    let sessionId = '';
    try { sessionId = new URL(url).searchParams.get('order_id') || ''; } catch { /* not a parseable URL */ }
    if (!/^[a-f0-9]{24}$/i.test(sessionId)) return;

    let stopped = false;
    const tick = async () => {
      if (stopped || handledRef.current) return;
      try {
        const r = await api.get(`/public/payment-status/${sessionId}`);
        if (stopped || handledRef.current) return;
        if (r.data?.status === 'completed') {
          handledRef.current = true;
          // Empty payload: the poll knows the charge landed, not its details.
          // Every onSuccess handler already tolerates this (they fall back to
          // the form's own amount and enrich from the backend when they need
          // a receipt number).
          onSuccessRef.current({});
        }
      } catch { /* network blip — keep polling */ }
    };
    const id = setInterval(tick, 4000);
    return () => { stopped = true; clearInterval(id); };
  }, [open, mode, url]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  // ─── BIT flow: gate on ת.ז., then initiate, then poll status ────────────
  // Clicking the BIT toggle no longer fires the SMS — it routes to a small
  // ת.ז.-entry step (mode='bit-id-entry'). Only after the donor provides a
  // valid 9-digit ID does sendBit() actually POST /initiate (which sends the
  // SMS gateway-side). Required because the donation receipt needs ת.ז. and
  // because the backend now enforces this on /payments/bit/initiate.
  const openBitGate = () => {
    if (!bitContext) return;
    if (!bitContext.donorPhone) {
      setBitErr('נדרש טלפון נייד לקבלת SMS לתשלום ב-BIT');
      setMode('bit-failed');
      return;
    }
    setBitErr(null);
    setMode('bit-id-entry');
  };

  const sendBit = async () => {
    if (!bitContext) return;
    const cleanId = (bitIdNumber || '').replace(/\D/g, '');
    if (!/^\d{9}$/.test(cleanId) || cleanId === '000000000') {
      setBitErr('יש להזין תעודת זהות בת 9 ספרות');
      return;
    }
    setMode('bit-confirming');
    setBitErr(null);
    try {
      const r = await api.post('/payments/bit/initiate', {
        orgId: bitContext.orgId,
        amount: bitContext.amount,
        campaignId: bitContext.campaignId,
        contactId: bitContext.contactId,
        ambassadorSlug: bitContext.ambassadorSlug,
        donorName: bitContext.donorName,
        donorEmail: bitContext.donorEmail,
        donorPhone: bitContext.donorPhone,
        donorIdNumber: cleanId,
        ...(bitContext.mailCampaignId ? { mailCampaignId: bitContext.mailCampaignId } : {}),
        ...(bitContext.tc ? { tc: bitContext.tc } : {}),
        ...(bitContext.customFields && Object.keys(bitContext.customFields).length
          ? { customFields: bitContext.customFields }
          : {}),
        ...(bitContext.anonymous ? { anonymous: true } : {}),
      });
      const sid = r.data?.sessionId;
      setBitSessionId(sid);
      setBitSchemes({ ios: r.data?.walletSchemeIos, android: r.data?.walletSchemeAndroid });
      setMode('bit-waiting');
    } catch (e: any) {
      setBitErr(e.response?.data?.error || e.message || 'שגיאה בפתיחת תשלום BIT');
      setMode('bit-failed');
    }
  };

  // Poll session status every 3s while waiting. Stops on terminal states.
  useEffect(() => {
    if (mode !== 'bit-waiting' || !bitSessionId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get(`/payments/bit/status/${bitSessionId}`);
        const status = r.data?.status;
        if (cancelled) return;
        if (status === 'completed') {
          setMode('bit-done');
          if (!handledRef.current) {
            handledRef.current = true;
            onSuccessRef.current({
              method: 'bit',
              donationId: r.data?.donationId || '',
              sessionId: bitSessionId,
            });
          }
        } else if (status === 'expired') {
          setMode('bit-failed');
          setBitErr('התשלום בוטל או פג תוקף');
        }
      } catch {
        // Network blip — keep polling.
      }
    };
    const id = setInterval(tick, 3000);
    tick();
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [mode, bitSessionId]);

  if (!open) return null;
  // For BIT-only flow we don't strictly need `url`, but for the card iframe
  // we do. If both are missing and we have bitContext we still show the BIT
  // launcher; otherwise nothing to render.
  if (!url && !bitContext) return null;

  const showBitToggle = bitAvailable && mode === 'card' && !!url && !bitContext?.recurring;
  // ת״ז already captured by the campaign form → the gate skips its own input.
  const bitIdPrefilled = /^\d{9}$/.test((bitContext?.donorIdNumber || '').replace(/\D/g, '')) &&
    (bitContext?.donorIdNumber || '').replace(/\D/g, '') !== '000000000';
  const isBitMode = mode !== 'card';

  return (
    <div
      // Bilingual: modal direction follows the donor's locale so the secure-
      // payment chrome reads naturally in English. Hebrew donors keep RTL
      // (legacy default, byte-identical to before).
      dir={getBootLocale() === 'en' ? 'ltr' : 'rtl'}
      className="fixed inset-0 z-[9999] flex items-center justify-center p-2 md:p-6"
      style={{ background: 'rgba(15,23,42,0.65)', backdropFilter: 'blur(4px)' }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col overflow-hidden"
        style={{ height: 'min(92vh, 780px)' }}
      >
        <div className="flex items-center justify-between px-4 md:px-5 py-3 border-b border-gray-200 bg-gradient-to-l from-blue-50 to-white shrink-0">
          <div className="flex items-center gap-2">
            <ShieldCheck size={20} className="text-blue-600" />
            <h2 className="text-base md:text-lg font-bold text-gray-900">{resolvedTitle}</h2>
          </div>
          <button
            onClick={onClose}
            aria-label="סגור"
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-800"
          >
            <X size={20} />
          </button>
        </div>

        {/* Breakdown strip — shown ONLY when the caller provides `details`.
            Visually attached to the header above and the iframe below so the
            entire modal reads as one screen, even though the gateway form
            comes from a different domain. */}
        {details && (
          <div className="px-4 md:px-5 py-3 bg-white border-b border-gray-200 shrink-0 max-h-[280px] overflow-y-auto">
            <div className="text-sm font-semibold text-gray-900 mb-1 truncate">
              {details.item}
            </div>
            {details.subline && (
              <div className="text-[11px] text-gray-500 mb-2">{details.subline}</div>
            )}
            {details.lines && details.lines.length > 0 && (
              <div className="space-y-1 mb-2.5">
                {details.lines.map((ln, idx) => (
                  <div key={idx} className="flex items-baseline justify-between gap-3 text-xs py-1 border-b border-gray-100 last:border-b-0">
                    <span className="text-gray-700 flex-1 min-w-0">
                      {ln.label}
                      {ln.note && <span className="text-gray-400 mr-1.5">· {ln.note}</span>}
                    </span>
                    <span className="font-semibold text-gray-800 whitespace-nowrap">
                      ₪{Number(ln.amount).toLocaleString('he-IL', { maximumFractionDigits: 2 })}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="text-center bg-gray-50 rounded-lg py-2 px-1">
                <div className="text-[10px] text-gray-500 mb-0.5">לפני מע״מ</div>
                <div className="font-semibold text-gray-800">
                  ₪{Number(details.net).toLocaleString('he-IL', { maximumFractionDigits: 2 })}
                </div>
              </div>
              <div className="text-center bg-gray-50 rounded-lg py-2 px-1">
                <div className="text-[10px] text-gray-500 mb-0.5">מע״מ 18%</div>
                <div className="font-semibold text-gray-800">
                  ₪{Number(details.vat).toLocaleString('he-IL', { maximumFractionDigits: 2 })}
                </div>
              </div>
              <div className="text-center bg-blue-50 border border-blue-200 rounded-lg py-2 px-1">
                <div className="text-[10px] text-blue-600 mb-0.5">סה״כ לתשלום</div>
                <div className="font-bold text-blue-700">
                  ₪{Number(details.gross).toLocaleString('he-IL', { maximumFractionDigits: 2 })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* BIT toggle — only when we're showing the credit-card iframe AND
            the system has BIT configured AND a bitContext was supplied. */}
        {showBitToggle && (
          <div className="px-4 md:px-5 py-2.5 bg-pink-50 border-b border-pink-200 flex items-center justify-between gap-3 shrink-0">
            <div className="flex items-center gap-2 text-sm text-pink-900">
              <Smartphone size={16} />
              <span>מעדיף לשלם דרך אפליקציית BIT?</span>
            </div>
            <button
              onClick={openBitGate}
              className="text-sm font-semibold text-pink-700 hover:text-pink-900 flex items-center gap-1"
            >
              שלם ב-BIT <ArrowRight size={14} />
            </button>
          </div>
        )}

        {/* Body — either the card iframe (default) or the BIT confirm screen. */}
        <div className="relative flex-1 bg-gray-50 overflow-auto">
          {!isBitMode && url && (
            <>
              {iframeLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
                  <div className="flex flex-col items-center gap-3 text-gray-500">
                    <Loader2 size={36} className="animate-spin text-blue-500" />
                    <span className="text-sm font-medium">טוען טופס תשלום מאובטח...</span>
                  </div>
                </div>
              )}
              <iframe
                ref={iframeRef}
                src={url}
                title="טופס תשלום"
                onLoad={() => setIframeLoading(false)}
                className="w-full h-full border-0"
                allow="payment *; publickey-credentials-get *"
                allowpaymentrequest={true as any}
              />
            </>
          )}

          {mode === 'bit-id-entry' && (
            <div className="h-full flex items-center justify-center p-6">
              <div className="max-w-sm w-full text-center space-y-4">
                <div className="mx-auto w-14 h-14 rounded-full bg-pink-100 flex items-center justify-center">
                  <Smartphone size={26} className="text-pink-600" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 mb-1">תשלום ב-BIT</h3>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    {bitIdPrefilled
                      ? 'נשלח אליך קישור ב-SMS לאישור התשלום באפליקציית Bit.'
                      : <>כדי להמשיך, הזן תעודת זהות (9 ספרות).<br />דרושה לקבלה ולקישור לכרטיס שלך.</>}
                  </p>
                </div>
                {bitIdPrefilled ? (
                  // ת״ז כבר מולאה בטופס — מציגים אישור ולא מבקשים שוב.
                  <div className="text-right bg-gray-50 border rounded-lg px-3 py-2">
                    <span className="text-xs text-gray-500">תעודת זהות</span>
                    <span className="block text-sm font-mono text-gray-800" dir="ltr">{bitIdNumber}</span>
                  </div>
                ) : (
                  <div className="text-right">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      תעודת זהות <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={9}
                      value={bitIdNumber}
                      onChange={(e) => { setBitIdNumber(e.target.value.replace(/\D/g, '')); if (bitErr) setBitErr(null); }}
                      placeholder="9 ספרות"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                      dir="ltr"
                      autoFocus
                    />
                    {bitErr && (
                      <p className="mt-2 text-xs text-red-600">{bitErr}</p>
                    )}
                  </div>
                )}
                {bitIdPrefilled && bitErr && (
                  <p className="text-xs text-red-600">{bitErr}</p>
                )}
                <button
                  onClick={sendBit}
                  disabled={bitIdNumber.length !== 9}
                  className="w-full bg-pink-600 hover:bg-pink-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg py-2.5 text-sm font-semibold"
                >
                  שלח קישור ב-SMS
                </button>
                <button
                  onClick={() => setMode('card')}
                  className="text-xs text-gray-500 hover:text-gray-700 underline"
                >
                  חזור לתשלום בכרטיס אשראי
                </button>
              </div>
            </div>
          )}

          {mode === 'bit-confirming' && (
            <div className="h-full flex items-center justify-center p-6">
              <div className="text-center text-gray-700 flex flex-col items-center gap-3">
                <Loader2 size={36} className="animate-spin text-pink-500" />
                <p className="font-medium">פותח תשלום BIT...</p>
              </div>
            </div>
          )}

          {mode === 'bit-waiting' && (
            <div className="h-full flex items-center justify-center p-6">
              <div className="max-w-sm text-center space-y-4">
                <div className="mx-auto w-16 h-16 rounded-full bg-pink-100 flex items-center justify-center">
                  <Smartphone size={28} className="text-pink-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">נשלח SMS לטלפון שלך</h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  פתח את ההודעה, הקש על הקישור ואשר את התשלום באפליקציית BIT.
                  המסך הזה יתעדכן אוטומטית ברגע שהתשלום יאושר.
                </p>
                {(bitSchemes.ios || bitSchemes.android) && (
                  <div className="pt-2">
                    <a
                      href={bitSchemes.ios || bitSchemes.android || '#'}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-pink-600 text-white text-sm font-semibold rounded-lg hover:bg-pink-700"
                    >
                      <Smartphone size={16} />
                      פתח באפליקציית BIT
                    </a>
                  </div>
                )}
                <div className="text-xs text-gray-500 flex items-center justify-center gap-2 pt-2">
                  <Loader2 size={12} className="animate-spin" />
                  ממתין לאישור...
                </div>
                <button
                  onClick={() => setMode('card')}
                  className="text-xs text-gray-500 hover:text-gray-700 underline pt-2"
                >
                  חזור לתשלום בכרטיס אשראי
                </button>
              </div>
            </div>
          )}

          {mode === 'bit-failed' && (
            <div className="h-full flex items-center justify-center p-6">
              <div className="max-w-sm text-center space-y-4">
                <h3 className="text-lg font-bold text-red-700">שגיאה בתשלום BIT</h3>
                <p className="text-sm text-gray-700">{bitErr || 'אירעה שגיאה. נסה שוב.'}</p>
                <button
                  onClick={() => setMode('card')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700"
                >
                  <CreditCard size={16} />
                  שלם בכרטיס אשראי
                </button>
              </div>
            </div>
          )}

          {mode === 'bit-done' && (
            <div className="h-full flex items-center justify-center p-6">
              <div className="max-w-sm text-center space-y-3">
                <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                  <ShieldCheck size={28} className="text-green-600" />
                </div>
                <h3 className="text-lg font-bold text-green-700">התשלום אושר</h3>
                <p className="text-sm text-gray-700">תודה על תרומתך!</p>
              </div>
            </div>
          )}
        </div>

        <div className="px-4 md:px-5 py-2.5 border-t border-gray-100 bg-gray-50 text-center shrink-0">
          <p className="text-[11px] md:text-xs text-gray-500">
            🔒 התשלום מתבצע בסליקה מאובטחת. Amutech לא שומרת פרטי אשראי.
          </p>
        </div>
      </div>
    </div>
  );
}
